/**
 * Packages, for an agent (RFC 0284 S5, consolidated in S6k).
 *
 * "Write me a sidebar that only lists my review pages" is the ask this exists
 * to serve. Everything under it was reachable from a button in Settings, which
 * proves the mechanism and does not make it useful: the whole point of the RFC
 * is that the good sidebar is the one somebody else writes.
 *
 * ONE INSTALL TOOL, and this file is the second half of the lesson the Packages
 * modal learned. There were three — `register_app`, `install_package`,
 * `install_package with kind 'thread'` — because each kind arrived in its own slice and a separate
 * tool was the smallest change each time. Five slices later that is a tool per
 * kind, an agent has to know which one a sidebar needs, and a sixth kind means
 * a sixth verb. `kind` is DATA, exactly as it is on a row in the modal.
 *
 * THE MUTATIONS STAY SEPARATE, deliberately. `registerApp` enforces one app per
 * data type, `installRenderer` refuses a second claim on `agent`,
 * `installLayout` mints no view document at all. Those are different writes
 * with different invariants, and collapsing them into one function with a
 * switch would be a worse function than the four. It is the agent-facing
 * SURFACE that should be one, not the write model under it.
 *
 * SCOPE FOLLOWS KIND. An app belongs to a project: it owns a data type and the
 * records in that repository. Everything else belongs to the World (RFC 0295),
 * because a window binds to a World and RFC 0290 lets it hold several projects,
 * so a sidebar installed per project would vanish the moment you switched.
 *
 * INSTALLING IS NOT ARRANGING. `set_slot` and `shape_slot` stay separate,
 * because they have different blast radii: installing adds something nobody
 * sees until it is named, arranging changes what is on screen right now. An
 * agent that could only do both at once could not fix a broken sidebar without
 * first showing it to you.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import {
  cleanIconSpec,
  generateIcon,
  restyleIcons,
} from '../model/apps/app-icon'
import {
  installChrome,
  installLayout,
  installRenderer,
  moveApp,
  registerApp,
  setLayoutGeometry,
  setLayoutSlots,
  unregisterApp,
  updateApp,
} from '../model/apps/mutations'
import {
  installedApps,
  layoutsIn,
  readSlotFills,
  slotGeometry,
} from '../model/apps/reads'
import { isReleaseId, publishApp, type Registry } from '../model/apps/registry'
import { installRelease } from '../model/apps/release'
import type { AppSchemaSpec, SettingSpec, Slot } from '../model/apps/types'
import { changeProject, projectString } from '../model/project/scalars'
import { projectWorldProjects } from '../model/world/reads'
import type { ToolDef } from './project-tools'

const SLOTS: Slot[] = ['sidebar', 'panel', 'header', 'composer']

/**
 * The kinds of package, which are the kinds the Packages modal badges.
 *
 * The tool's `kind` and the row's label are the same word on purpose. An agent
 * told "install a sidebar" and a person looking at a row that says "Sidebar"
 * should be talking about the same thing.
 */
const KINDS = [
  'app',
  'sidebar',
  'header',
  'panel',
  'composer',
  'thread',
  'layout',
] as const
type PackageKind = (typeof KINDS)[number]

async function ready(handle: { whenReady?: () => Promise<void> }) {
  await handle.whenReady?.()
}

export function packageTools(
  repo: Repo,
  /**
   * `worldId` is OPTIONAL, and that is a correctness requirement rather than a
   * convenience. `register_app` used to live in `appTools`, which every host
   * gets; folding it into this tool while this tool was gated on a World would
   * have left a World-less host — the Cursor MCP child, a bare cli — unable to
   * register an app at all. An app installs in the PROJECT and needs no World.
   */
  opts: {
    worldId?: string | null
    projectId: string
    /** Where `publish_package` posts (RFC 0279 S3). Absent on a host that
     *  has no sign-in to publish with, and the tool says so. */
    registry?: Registry
  },
): Record<string, ToolDef> {
  /** The World, or a refusal that names what is missing. Chrome, layouts and
   *  thread renderers have nowhere to live without one (RFC 0295). */
  const requireWorld = (): string => {
    if (!opts.worldId)
      throw new Error(
        'this host has no World, so only `kind: "app"` can be installed. ' +
          'Chrome, layouts and thread renderers live in the World (RFC 0295) ' +
          'so they follow a person across projects.',
      )
    return opts.worldId
  }

  const readWorld = async () => {
    const handle = await repo.find(requireWorld() as AnyDocumentId)
    await ready(handle as { whenReady?: () => Promise<void> })
    return handle
  }

  /**
   * BOTH CATALOGS, for listing and uninstalling (S6k).
   *
   * Chrome installs in the World and an app installs in the project, so a tool
   * that read one of them could only see some of what it claims to list — and
   * `unregisterApp` takes the catalog to remove from, so removing against the
   * wrong one edits a map that never held the entry and appears to do nothing.
   * `installedApps` stamps `scope`, which is the only thing that knows.
   */
  const readAll = async () => {
    const project = await repo.find(opts.projectId as AnyDocumentId)
    await ready(project as { whenReady?: () => Promise<void> })
    // A World-less host still has a project catalog, and listing what it holds
    // must not fail because the other half is absent.
    if (!opts.worldId) return installedApps(undefined, project.doc())
    const world = await readWorld()
    return installedApps(world.doc(), project.doc())
  }

  /** Which catalog holds an app: this project, the World, or another of the
   *  World's projects. `null` for none. */
  const catalogHolding = async (appId: string): Promise<string | null> => {
    const has = async (id: string) => {
      const h = await repo.find(id as AnyDocumentId)
      await ready(h as { whenReady?: () => Promise<void> })
      return !!(h.doc() as { apps?: Record<string, unknown> })?.apps?.[appId]
    }
    if (await has(opts.projectId)) return opts.projectId
    if (!opts.worldId) return null
    if (await has(opts.worldId)) return opts.worldId
    for (const { projectId } of projectWorldProjects(
      (await readWorld()).doc(),
    )) {
      if (projectId !== opts.projectId && (await has(projectId)))
        return projectId
    }
    return null
  }

  /**
   * ONE OWNER PER DATA TYPE across everything a project sees (RFC 0338 S6).
   *
   * `registerApp` checks the catalog it writes, which was the whole rule while
   * apps lived in projects only. With World apps, a project app and a World
   * app on one type both resolve for that project's records, and which draws
   * them is array order. So a project install checks the World, and a World
   * install or move checks the World and every project in it. `except` is the
   * app being moved, which owns its own type already.
   */
  const typeOwner = async (
    dataType: string,
    catalogs: readonly string[],
    except?: string,
  ): Promise<{ appId: string; name: string; catalogId: string } | null> => {
    if (!dataType) return null
    for (const id of catalogs) {
      const h = await repo.find(id as AnyDocumentId)
      await ready(h as { whenReady?: () => Promise<void> })
      const apps =
        (
          h.doc() as {
            apps?: Record<string, { dataType?: string; name?: string }>
          }
        )?.apps ?? {}
      for (const [appId, ref] of Object.entries(apps)) {
        if (appId !== except && projectString(ref?.dataType) === dataType)
          return { appId, name: String(ref.name ?? appId), catalogId: id }
      }
    }
    return null
  }
  /** The World and every project it lists. */
  const everyCatalog = async (): Promise<string[]> => {
    const worldId = requireWorld()
    const projects = projectWorldProjects((await readWorld()).doc()).map(
      (p) => p.projectId,
    )
    return [worldId, ...new Set([opts.projectId, ...projects])]
  }
  const refuseOwner = (
    dataType: string,
    owner: { appId: string; name: string; catalogId: string },
  ): never => {
    const where =
      owner.catalogId === opts.worldId
        ? 'the World'
        : `project ${owner.catalogId}`
    throw new Error(
      `"${owner.name}" (${owner.appId}) in ${where} already owns dataType ` +
        `"${dataType}". Two apps on one type would both draw its records, ` +
        'and which one wins would be array order.',
    )
  }

  /** The registry, or a refusal that says where publishing and the registry work. */
  const requireRegistry = (): Registry => {
    if (!opts.registry)
      throw new Error(
        'this host cannot reach the registry. Use the desktop app, or ' +
          '`mod-next app publish` to publish from a folder.',
      )
    return opts.registry
  }

  /** Lineages already here, installed from the registry or published from here. */
  const installedLineages = async (): Promise<Set<string>> => {
    const out = new Set<string>()
    for (const app of await readAll()) {
      const h = await repo.find(app.id as AnyDocumentId)
      await ready(h as { whenReady?: () => Promise<void> })
      const d = h.doc() as
        | {
            installedFrom?: { lineageId?: unknown }
            publishedAs?: { lineageId?: unknown }
          }
        | undefined
      for (const l of [d?.installedFrom?.lineageId, d?.publishedAs?.lineageId])
        if (l) out.add(String(l))
    }
    return out
  }

  /**
   * INSTALL FROM THE REGISTRY (RFC 0279 S5): a release id, or a package's exact
   * name or lineage id. A name that matches several is named back rather than
   * guessed, since installing the wrong one of two polls is worse than asking.
   */
  const installFromRegistry = async (
    wanted: string,
    scope: unknown,
  ): Promise<string> => {
    const registry = requireRegistry()
    let releaseId = wanted
    if (!isReleaseId(wanted)) {
      const entries = await registry.list(wanted)
      const lower = wanted.toLowerCase()
      const exact = entries.filter(
        (e) => e.lineageId === wanted || e.name.toLowerCase() === lower,
      )
      if (exact.length !== 1) {
        const pick = (exact.length ? exact : entries).slice(0, 5)
        throw new Error(
          `${exact.length ? 'several packages are' : 'no package is'} named ` +
            `"${wanted}" in the registry.` +
            (pick.length
              ? ` Pass one release id:\n${pick
                  .map(
                    (e) =>
                      `${e.latest.releaseId}  ${e.name} ${e.latest.version} ` +
                      `(${e.lineageId}, by ${e.publisher.name || 'unknown'})`,
                  )
                  .join('\n')}`
              : ' list_packages with available: true shows what there is.'),
        )
      }
      releaseId = exact[0].latest.releaseId
    }
    const release = await registry.release(releaseId)
    // Chrome lives in the World (RFC 0295); an app goes there too unless the
    // caller asks for this project, which is RFC 0279 3e's default.
    const world =
      release.kind !== 'app' ? requireWorld() : (opts.worldId ?? undefined)
    const appId = await installRelease(
      repo,
      {
        projectId: opts.projectId,
        ...(world ? { worldId: world } : {}),
        scope:
          scope === 'project' && release.kind === 'app' ? 'project' : 'world',
      },
      release,
      releaseId,
    )
    const where =
      world && !(scope === 'project' && release.kind === 'app')
        ? 'the World'
        : 'this project'
    return (
      `Installed "${release.name}" ${release.version} from the registry into ` +
      `${where} (app ${appId}).` +
      (['sidebar', 'header', 'composer'].includes(release.kind)
        ? `\nIt draws nothing until a layout names it: set_slot with fill ` +
          `"${release.panels?.find((p) => p.slot === release.kind)?.id ?? release.lineageId}".`
        : '')
    )
  }

  return {
    install_package: {
      description:
        'Install a PACKAGE. One tool for every kind, because the kind is a ' +
        'field rather than a different verb.\n' +
        'FROM THE REGISTRY FIRST: when asked for an app, run list_packages with ' +
        'available: true and a query, and if something there fits, install ' +
        'it with `release` (its release id or exact name) and nothing else. ' +
        'Build one only when nothing fits.\n' +
        'Otherwise pick `kind`:\n' +
        "  'app'      a thing that owns records and draws them. Needs " +
        'dataType, viewDocId and skillDocId, which you author with ' +
        'create_document first. Goes in THIS PROJECT unless `scope` is ' +
        "'world'; an app installed already moves to the World with " +
        "{ kind: 'app', scope: 'world', appId }.\n" +
        "  'sidebar' | 'header' | 'panel' | 'composer'   chrome: a module " +
        'that draws that surface instead of the one Mod ships. Needs `code`. ' +
        'Installing does NOT show it: follow with set_slot.\n' +
        "  The 'composer' slot is a strip along the BOTTOM of the content " +
        'area (S8c). The shipped one draws only in a space with no thread ' +
        'open in it, so a person can type without spending a pane first; a ' +
        'replacement decides that for itself.\n' +
        "  'thread'   a module that draws a SESSION instead of the shipped " +
        'thread. Needs `code`. Takes effect at once in every session pane, ' +
        'and there can be only one.\n' +
        "  'layout'   an arrangement: which fill fills each slot. No " +
        'code, nothing to run. A person selects it in Settings (⌘,).\n' +
        'Everything except an app is installed in the WORLD, so it follows a ' +
        'person across the projects this window holds.\n\n' +
        'WRITING `code` (chrome and thread). An ES module exporting default a ' +
        'mount function, called with { container, window, docs, sessions, ' +
        'settings, session, onDispose }. `container` is an empty element to ' +
        'draw into with plain DOM. `docs` is what the person HAS: list() ' +
        'gives every document as { id, projectId, title, path, mime, ' +
        'updatedAt, dataType }, onChange(fn) fires when it changes. ' +
        '`sessions` is their THREADS across every project this window holds: ' +
        'list() gives { id, title, projectId, projectName, updatedAt, ' +
        'completedAt }, onChange(fn) likewise; there is no status field yet. ' +
        '`settings` is what a person configured this package to do: ' +
        'get(key), all(), onChange(fn) — declare them with `settings` below, ' +
        'and prefer one over a constant for anything somebody might want ' +
        'different, since the alternative is them asking you to rewrite the ' +
        'module to change a number. `window` is what is ON SCREEN: read() ' +
        'gives { leaves, focused, space } where each leaf is ' +
        '{ leafId, content, focused }; open(subject, where) takes ' +
        "{ kind: 'document'|'agent', id, projectId } and an optional " +
        "{ leafId, edge: 'left'|'right'|'up'|'down' }; close(leafId), " +
        'focus(leafId), setSpace(spaceId), onChange(fn). `session` is passed ' +
        'ONLY to a thread: read() gives { id, projectId, transcript } and ' +
        'onChange(fn) fires as the agent replies; a null id means the pane ' +
        'has no session selected, which you must draw rather than treat as ' +
        'an error. A thread gets NO composer: drawing a session is a view, ' +
        'submitting a turn is not, and the shell keeps its own composer ' +
        'below your mount.\n' +
        'SUBSCRIBE TO EVERY SOURCE YOU READ and redraw, or it goes stale in a ' +
        'way that reads as broken rather than as late.\n' +
        'LAYOUT: do not make a scrolling root a flex column. Flex items ' +
        'shrink, so a list taller than the column clips every row to a few ' +
        'pixels and the text reads as garbled rather than as overflowing. ' +
        'Use a block container with overflow-y:auto, or flex-shrink:0 on ' +
        'every row. The same trap rotated 90 degrees applies to a header.',
      inputSchema: {
        type: 'object',
        properties: {
          release: {
            type: 'string',
            description:
              'Install from the registry: a release id, or a package name or ' +
              "lineage id from list_packages. Installs into the World by default; pass scope: 'project' to keep an app in this project.",
          },
          kind: {
            type: 'string',
            description: `One of: ${KINDS.join(', ')}.`,
          },
          name: { type: 'string', description: 'Shown in Packages.' },
          description: { type: 'string' },
          code: {
            type: 'string',
            description: 'The module source. Chrome and thread only.',
          },
          settings: {
            type: 'array',
            description:
              'What a person can configure, as ' +
              '[{ key, label, type, default, description }]. `type` is ' +
              "'string', 'number' or 'boolean'. Read them back with " +
              'settings.get(key). Chrome and thread only.',
          },
          scope: {
            type: 'string',
            description:
              "App only. For a registry release, 'world' is the default; pass 'project' to install only here. For a locally built app, 'project' is the default. World installs reach every project.",
          },
          appId: {
            type: 'string',
            description:
              "App only, with scope 'world'. An app installed in a project " +
              'already: its entry moves into the World, keeping its id. No ' +
              'other field is needed.',
          },
          dataType: {
            type: 'string',
            description: 'App only. The record type this app owns.',
          },
          viewDocId: {
            type: 'string',
            description: 'App only. From create_document.',
          },
          skillDocId: {
            type: 'string',
            description: 'App only. From create_document.',
          },
          schema: {
            type: 'object',
            description: 'App only. { fields, example }.',
          },
          reads: {
            type: 'array',
            items: { type: 'string' },
            description:
              'App only. Built-in interfaces it reads: documents, sessions, ' +
              'vcs, comments.',
          },
          runtime: {
            type: 'string',
            description: 'App only. View runtime: "html" (default), "react@1".',
          },
          presentation: {
            type: 'string',
            description:
              'App only. "document" lists its instances beside documents ' +
              '(RFC 0271) rather than as an application.',
          },
          instancesOpenable: {
            type: 'boolean',
            description:
              "App only. List and open this app's records on their own, " +
              'WITHOUT giving up its index. Set this when a record is a thing ' +
              'somebody links to and puts in a pane (a canvas, a review) while ' +
              'the app still has a useful index of them. Leave it off for ' +
              'records that are only rows in that index (a calendar event, a ' +
              "CRM contact): listing those beside the project's documents " +
              'buries the documents. A "document" app is already openable and ' +
              'ignores it.',
          },
          panels: {
            type: 'array',
            description:
              'App only. Sections it adds to the canvas side column ' +
              "(RFC 0273), as [{ id, title, kind: 'view' }].",
          },
          workerName: {
            type: 'string',
            description: 'Optional background worker name.',
          },
          slots: {
            type: 'object',
            description:
              'Layout only. Slot to fill ids. An empty array EMPTIES ' +
              'that slot; a slot left out falls back to the built-in.',
          },
        },
        required: [],
      },
      execute: async (a) => {
        if (a.release) return installFromRegistry(String(a.release), a.scope)
        const kind = String(a.kind ?? '') as PackageKind
        if (!KINDS.includes(kind))
          // NAMED BACK, rather than guessed at. A package filed under a kind
          // nobody asked for draws nowhere and looks like the install failed.
          throw new Error(
            `unknown kind "${a.kind}". One of: ${KINDS.join(', ')}.`,
          )
        const name = String(a.name ?? '').trim()
        if (!name && !(kind === 'app' && a.appId))
          throw new Error('name is required')
        const description = a.description ? String(a.description) : ''
        const settings = Array.isArray(a.settings)
          ? (a.settings as SettingSpec[])
          : undefined

        /** Chrome and thread both need a module, and neither can use a bad one. */
        const moduleSource = () => {
          const code = String(a.code ?? '')
          if (!code.includes('export default'))
            throw new Error(
              'code must export default a mount function; nothing else is ' +
                'called, so a module without one installs and never draws.',
            )
          return code
        }

        if (kind === 'app') {
          const scope = a.scope === undefined ? 'project' : String(a.scope)
          if (scope !== 'project' && scope !== 'world')
            throw new Error(
              `unknown scope "${a.scope}". One of: project, world.`,
            )
          // An app ALREADY INSTALLED somewhere, put in the World (RFC 0338
          // 3g). Its entry moves rather than a second registration, because
          // its records, settings and a person's "Open with" all name its id.
          const existing = String(a.appId ?? '').trim()
          if (existing) {
            if (scope !== 'world')
              throw new Error(
                'appId installs an app that exists already into the World; ' +
                  "pass scope: 'world'",
              )
            const worldId = requireWorld()
            const from = await catalogHolding(existing)
            if (!from)
              throw new Error(
                `${existing} is not an app installed in this project or any ` +
                  'project of this World',
              )
            if (from === worldId)
              return `${existing} is already installed in the World.`
            const moving = (
              (await repo.find(from as AnyDocumentId)).doc() as {
                apps?: Record<string, { dataType?: string; name?: string }>
              }
            )?.apps?.[existing]
            const movingType = String(moving?.dataType ?? '')
            const owner = await typeOwner(
              movingType,
              await everyCatalog(),
              existing,
            )
            if (owner) refuseOwner(movingType, owner)
            if (!(await moveApp(repo, existing, from, worldId)))
              throw new Error(
                `${existing} was no longer in ${from} when the move ran; nothing moved`,
              )
            // A copy left in another project would shadow the World entry
            // there and go stale (RFC 0338 S7): the World holds it now.
            for (const id of await everyCatalog()) {
              if (id === worldId) continue
              const h = await repo.find(id as AnyDocumentId)
              await ready(h as { whenReady?: () => Promise<void> })
              if (
                (h.doc() as { apps?: Record<string, unknown> })?.apps?.[
                  existing
                ]
              )
                changeProject(h, (d: { apps?: Record<string, unknown> }) => {
                  if (d.apps) delete d.apps[existing]
                })
            }
            return (
              `Installed ${moving?.name ?? existing} (${existing}) in the World. ` +
              'Every project now lists and opens its records.'
            )
          }
          const dataType = String(a.dataType ?? '').trim()
          const viewDocId = String(a.viewDocId ?? '').trim()
          const skillDocId = String(a.skillDocId ?? '').trim()
          if (!dataType) throw new Error('an app needs a dataType')
          if (!viewDocId)
            throw new Error(
              'an app needs a viewDocId; author it with create_document',
            )
          if (!skillDocId)
            throw new Error(
              'an app needs a skillDocId; author it with create_document',
            )
          const raw =
            a.schema && typeof a.schema === 'object'
              ? (a.schema as { fields?: unknown; example?: unknown })
              : {}
          const schema: AppSchemaSpec = {
            fields:
              raw.fields && typeof raw.fields === 'object'
                ? (raw.fields as Record<string, string>)
                : {},
            ...(raw.example && typeof raw.example === 'object'
              ? { example: raw.example as Record<string, unknown> }
              : {}),
          }
          // THIS PROJECT unless asked for the World. Either way, no other app
          // the project can see may own the type.
          const owner = await typeOwner(
            dataType,
            scope === 'world'
              ? await everyCatalog()
              : opts.worldId
                ? [opts.worldId]
                : [],
          )
          if (owner) refuseOwner(dataType, owner)
          const appId = await registerApp(repo, opts.projectId, {
            // The app's documents stay in this project either way; only the
            // catalog entry says who sees it (RFC 0295 4a).
            ...(scope === 'world' ? { catalogId: requireWorld() } : {}),
            name,
            description,
            dataType,
            schema,
            viewDocId,
            skillDocId,
            capabilities: {
              writes: [dataType],
              reads: Array.isArray(a.reads) ? a.reads.map(String) : [],
            },
            ...(a.runtime ? { runtime: String(a.runtime) } : {}),
            ...(a.workerName ? { workerName: String(a.workerName) } : {}),
            ...(a.presentation === 'document'
              ? { presentation: 'document' as const }
              : {}),
            // PR #264, carried across the merge that deleted `register_app`.
            ...(a.instancesOpenable === true
              ? { instancesOpenable: true }
              : {}),
            // VALIDATED rather than passed through: a catalog entry is read
            // on every render by every client. readSlotFills drops what it
            // cannot draw and keeps each fill's slot and projection.
            ...(() => {
              const panels = readSlotFills(a.panels)
              return panels.length ? { panels } : {}
            })(),
          })
          return scope === 'world'
            ? `Installed app ${appId} (${name}, type "${dataType}") in the World, for every project.`
            : `Installed app ${appId} (${name}, type "${dataType}").`
        }

        if (kind === 'layout') {
          const layoutId = await installLayout(repo, requireWorld(), {
            name,
            description,
            slots:
              a.slots && typeof a.slots === 'object'
                ? (a.slots as Partial<Record<Slot, string[]>>)
                : {},
          })
          return (
            `Installed layout ${layoutId}.\n` +
            'A person selects it in Settings (⌘,) under Layout, because which ' +
            'layout a SPACE uses is theirs to choose.'
          )
        }

        if (kind === 'thread') {
          // ALLOWED TO STACK, since S6m. This refused a second claimant on the
          // grounds that resolution was a coin flip; it is not any more, and
          // refusing made trying a different thread mean uninstalling the one
          // you had. Several claimants with no choice draws the BUILT-IN, which
          // is honest, so the person is told to go and pick.
          const already = installedApps(
            (await readWorld()).doc(),
            undefined,
          ).find((app) => app.handles?.includes('agent'))
          const worldId = requireWorld()
          const { appId } = await installRenderer(repo, worldId, {
            name,
            description,
            handles: 'agent',
            code: moduleSource(),
          })
          return already
            ? `Installed "${name}" as a thread renderer.\napp=${appId}\n` +
                `"${already.name}" also draws sessions, so neither is used ` +
                'until somebody picks: with two claimants and no choice the ' +
                'SHIPPED thread draws, because guessing between them would be ' +
                'a coin flip. A person chooses in Settings (⌘,) under "Open ' +
                'with".'
            : `Installed "${name}" as the thread renderer.\napp=${appId}\n` +
                'It draws every session pane from now on. A person can switch ' +
                'back to the shipped thread in Settings (⌘,) under "Open ' +
                'with", without uninstalling this.'
        }

        const slot = kind as Slot
        const { appId, fillId } = await installChrome(repo, requireWorld(), {
          name,
          description,
          slot,
          code: moduleSource(),
          settings,
        })
        return (
          `Installed "${name}" for the ${slot} slot.\n` +
          `app=${appId}  fill=${fillId}\n` +
          'Nothing changed on screen yet. Call set_slot with this ' +
          'fill id to put it in the slot.'
        )
      },
    },

    set_slot: {
      description:
        'Put a fill in a chrome slot, for the layout this space uses ' +
        '(RFC 0284). Makes a layout if there is none. Pass `fillId` ' +
        'to use one, omit it and pass `builtin: true` to go back to the ' +
        'sidebar Mod ships, or `empty: true` for no sidebar at all. A person ' +
        'can always undo this in Settings (⌘,).',
      inputSchema: {
        type: 'object',
        properties: {
          slot: { type: 'string', description: "Default 'sidebar'." },
          fillId: {
            type: 'string',
            description: 'From install_package, or list_packages.',
          },
          builtin: { type: 'boolean' },
          empty: { type: 'boolean' },
          layoutName: {
            type: 'string',
            description: 'Names the layout, if one has to be made.',
          },
        },
      },
      execute: async (a) => {
        const slot = String(a.slot ?? 'sidebar') as Slot
        if (!SLOTS.includes(slot))
          throw new Error(
            `unknown slot "${a.slot}". One of: ${SLOTS.join(', ')}.`,
          )

        // THREE STATES, matching what the shell reads. Absent means the
        // built-in draws, [] means the slot is emptied, and ids name
        // fills. Conflating the first two is the bug the whole slot
        // resolver exists to prevent.
        const fill: string[] | undefined = a.builtin
          ? undefined
          : a.empty
            ? []
            : a.fillId
              ? [String(a.fillId)]
              : undefined

        const world = await readWorld()
        const layouts = layoutsIn(installedApps(world.doc(), undefined))
        const existing = layouts[0]

        if (existing) {
          const slots = { ...(existing.layout?.slots ?? {}) }
          if (fill === undefined) delete slots[slot]
          else slots[slot] = fill
          await setLayoutSlots(repo, requireWorld(), existing.id, slots)
          return `Layout "${existing.name}" now ${describe(slot, fill)}.`
        }

        const layoutId = await installLayout(repo, requireWorld(), {
          name: a.layoutName ? String(a.layoutName) : 'Custom',
          description: 'Made to hold a chrome fill.',
          slots: fill === undefined ? {} : { [slot]: fill },
        })
        return (
          `Made layout ${layoutId}, which ${describe(slot, fill)}.\n` +
          'A person has to select it in Settings (⌘,) under Layout for it to ' +
          'take effect, because which layout a SPACE uses is theirs to choose.'
        )
      },
    },

    shape_slot: {
      description:
        'Change how a chrome slot is SHAPED, for the layout this space uses ' +
        '(RFC 0284 S6d): how tall or wide it opens, and whether it is pinned ' +
        'or only appears when the pointer reaches the edge. Separate from ' +
        'set_slot because filling a slot and sizing it are different acts: ' +
        'a module is drawn into a box it cannot resize, so this is the only ' +
        'way to make a taller header or one that is not there until reached ' +
        'for. Only the HEADER is honoured today; the sidebar is sized by ' +
        'dragging and collapsed with ⌘B, both device-local.',
      inputSchema: {
        type: 'object',
        properties: {
          slot: { type: 'string', description: "Default 'header'." },
          size: {
            type: 'number',
            description:
              'CSS pixels. Height for the header. Omit, or pass 0, to go ' +
              'back to the default.',
          },
          reveal: {
            type: 'string',
            description:
              "'pinned' holds the space open. 'hover' takes the surface out " +
              'of the layout so the panes get the space, and floats it over ' +
              'them when the pointer reaches the edge.',
          },
        },
      },
      execute: async (a) => {
        const slot = String(a.slot ?? 'header') as Slot
        if (!SLOTS.includes(slot))
          throw new Error(
            `unknown slot "${a.slot}". One of: ${SLOTS.join(', ')}.`,
          )
        const reveal = a.reveal === undefined ? undefined : String(a.reveal)
        if (reveal !== undefined && reveal !== 'pinned' && reveal !== 'hover')
          throw new Error(
            `reveal must be 'pinned' or 'hover'; got "${reveal}".`,
          )

        const world = await readWorld()
        const apps = installedApps(world.doc(), undefined)
        const layout = layoutsIn(apps)[0]
        if (!layout)
          throw new Error(
            'no layout to shape. set_slot makes one, or a person makes one ' +
              'in Settings (⌘,) under Layout.',
          )

        const size =
          typeof a.size === 'number' && a.size > 0 ? a.size : undefined
        // READ THE REST BACK, because setLayoutGeometry replaces wholesale.
        // Shaping the header must not silently unshape the sidebar.
        const all = { ...(layout.layout?.geometry ?? {}) }
        const next = {
          size,
          reveal: (reveal ?? slotGeometry(layout.layout, slot).reveal) as
            | 'pinned'
            | 'hover',
        }
        // ABSENT rather than a record of two defaults, so a layout only claims
        // decisions somebody made.
        if (next.size === undefined && next.reveal === 'pinned')
          delete all[slot]
        else all[slot] = next
        await setLayoutGeometry(repo, requireWorld(), layout.id, all)

        return (
          `Layout "${layout.name}": the ${slot} is now ` +
          `${next.size ? `${next.size}px` : 'the default size'} and ` +
          `${next.reveal === 'hover' ? 'appears on hover' : 'always shown'}.\n` +
          'A person can change both in Settings (⌘,).'
        )
      },
    },

    uninstall_package: {
      description:
        'Uninstall a package of any kind, and take it out of any layout that ' +
        'names it. Takes the app id, or a chrome fill id — either, ' +
        'since a caller holds whichever list_packages gave it. A package left ' +
        'installed but unused still shows in Packages, and several with the ' +
        'same name are indistinguishable there.',
      inputSchema: {
        type: 'object',
        properties: {
          id: {
            type: 'string',
            description: 'An app id or a fill id, from list_packages.',
          },
        },
        required: ['id'],
      },
      execute: async (a) => {
        const fillId = String(a.id ?? a.fillId ?? '')
        const apps = await readAll()
        // BY CONTRIBUTION ID, or by APP id. A chrome fill is named
        // inside its app's `panels`; a thread renderer has no fill and
        // is named by the app itself, so this end takes either.
        const owner = apps.find(
          (app) =>
            app.id === fillId ||
            (app.panels ?? []).some((c) => c.id === fillId),
        )
        if (!owner)
          throw new Error(
            `no package "${fillId}". list_packages shows what is ` +
              'installed.',
          )

        /**
         * TAKE IT OUT OF EVERY LAYOUT FIRST.
         *
         * A layout naming a fill that no longer resolves renders an
         * EMPTY slot, because an id that resolves to nothing is dropped rather
         * than substituted. Uninstalling without this would leave a person
         * with no sidebar and no clue why, which is the RFC's open question
         * arriving as a bug.
         *
         * AN EMPTIED LIST BECOMES ABSENT, not `[]`. Removing the last id means
         * the person no longer named anything, which is "use the built-in",
         * not "I want no sidebar". Writing `[]` would take their sidebar away
         * as a side effect of tidying up.
         */
        const touched: string[] = []
        for (const layout of layoutsIn(apps)) {
          const slots = { ...(layout.layout?.slots ?? {}) }
          let changed = false
          for (const key of Object.keys(slots) as Slot[]) {
            const fill = slots[key]
            if (!fill?.includes(fillId)) continue
            const next = fill.filter((id) => id !== fillId)
            if (next.length === 0) delete slots[key]
            else slots[key] = next
            changed = true
          }
          if (!changed) continue
          await setLayoutSlots(repo, requireWorld(), layout.id, slots)
          touched.push(layout.name)
        }

        // THE CATALOG IT ACTUALLY LIVES IN. An app is a project's; everything
        // else is the World's.
        await unregisterApp(
          repo,
          owner.scope === 'project' ? opts.projectId : requireWorld(),
          owner.id,
        )

        return (
          `Uninstalled "${owner.name}" (${fillId}).` +
          (touched.length
            ? `\nRemoved from ${touched.length} layout(s): ${touched.join(', ')}. ` +
              'Any slot it was the only fill for is back to the built-in.'
            : '\nNo layout named it, so nothing on screen changed.')
        )
      },
    },

    publish_package: {
      description:
        'Publish an installed app or sidebar to the registry (RFC 0279), so ' +
        'anyone can find and install it. Copies its view and skill as they ' +
        'are now; records never travel. Publishing it again adds a version ' +
        'to the same package. Ask the person before publishing: it is public. ' +
        'An app you wrote in a conversation publishes its view and skill as ' +
        'source for forking unless include_source is false; a pushed folder ' +
        'includes its files only when include_source is true.',
      inputSchema: {
        type: 'object',
        properties: {
          id: {
            type: 'string',
            description: 'The app id or sidebar fill id, from list_packages.',
          },
          category: {
            type: 'string',
            description: 'One word for the registry, e.g. "Work", "Writing".',
          },
          about: {
            type: 'string',
            description:
              'What its page says it does. Defaults to its description.',
          },
          screenshots: {
            type: 'array',
            items: { type: 'string' },
            description: 'https URLs of images for its page.',
          },
          version: {
            type: 'string',
            description:
              'MAJOR.MINOR.PATCH. Defaults to 1.0.0, then a patch bump.',
          },
          lineage_id: {
            type: 'string',
            description:
              'The package to add a version to, e.g. "mod.html". Defaults ' +
              'to the one it was last published as, or a new one. Pass it ' +
              'when a copy that does not remember its package would ' +
              'otherwise publish a duplicate.',
          },
          include_source: {
            type: 'boolean',
            description:
              'Publish its source for forking. Defaults to true for an app ' +
              'written in a conversation, false for a pushed folder.',
          },
        },
        required: ['id', 'category'],
      },
      execute: async (a) => {
        const registry = requireRegistry()
        const id = String(a.id ?? '')
        const apps = await readAll()
        const app = apps.find(
          (x) => x.id === id || (x.panels ?? []).some((p) => p.id === id),
        )
        if (!app) throw new Error(`nothing installed is "${id}"`)
        const published = await publishApp(repo, {
          catalogId: app.scope === 'world' ? requireWorld() : opts.projectId,
          appId: app.id,
          registry,
          page: {
            category: String(a.category ?? ''),
            ...(a.about ? { about: String(a.about) } : {}),
            ...(Array.isArray(a.screenshots)
              ? { screenshots: a.screenshots.map(String) }
              : {}),
          },
          ...(a.version ? { version: String(a.version) } : {}),
          ...(a.lineage_id ? { lineageId: String(a.lineage_id) } : {}),
          ...(typeof a.include_source === 'boolean'
            ? { includeSource: a.include_source }
            : {}),
        })
        return (
          `Published "${app.name}" ${published.version} to the registry as ` +
          `${published.lineageId} (release ${published.releaseId}).`
        )
      },
    },

    list_packages: {
      description:
        'Everything installed, of every kind: apps, chrome fills, ' +
        'thread renderers and layouts. Read this before installing a second ' +
        'of anything — updating the one that exists beats leaving two nobody ' +
        'can tell apart.',
      inputSchema: {
        type: 'object',
        properties: {
          available: {
            type: 'boolean',
            description:
              'List what is in the registry and not installed here, instead ' +
              'of what is installed (RFC 0279).',
          },
          query: {
            type: 'string',
            description: 'With available: words to search the registry for.',
          },
        },
      },
      execute: async (a) => {
        if (a.available) {
          const entries = await requireRegistry().list(
            a.query ? String(a.query) : undefined,
          )
          const here = await installedLineages()
          const fresh = entries.filter((e) => !here.has(e.lineageId))
          if (!fresh.length)
            return entries.length
              ? 'Everything in the registry that matches is installed already.'
              : 'Nothing in the registry matches.'
          return fresh
            .map(
              (e) =>
                `${e.latest.releaseId}  ${e.name} ${e.latest.version}  ` +
                `kind=${e.kind}  ${e.category}  by ${e.publisher.name || 'unknown'}\n` +
                `  ${e.about || e.description}`,
            )
            .join('\n')
        }
        const apps = await readAll()
        const fills = apps.flatMap((app) =>
          (app.panels ?? []).map(
            (c) => `${c.id}  ${c.title || app.name}  slot=${c.slot ?? 'panel'}`,
          ),
        )
        const renderers = apps
          .filter((a) => a.handles?.length)
          .map((a) => `${a.id}  ${a.name}  handles=${a.handles?.join(', ')}`)
        // APPS TOO, since S6k. This listed everything except the kind a person
        // is most likely to have, because it grew out of a chrome-only tool.
        const plain = apps
          .filter(
            (a) =>
              a.presentation !== 'layout' &&
              !a.handles?.length &&
              // A pure chrome fill is listed under Fills. An app with its own
              // data type stays an app even when it also fills a slot.
              !(!a.dataType && (a.panels ?? []).some((c) => c.slot)),
          )
          .map((a) => `${a.id}  ${a.name}  type=${a.dataType || '(none)'}`)
        const layouts = layoutsIn(apps).map(
          (l) =>
            `${l.id}  ${l.name}  slots=${JSON.stringify(l.layout?.slots ?? {})}`,
        )
        return [
          plain.length ? `Apps:\n${plain.join('\n')}` : '(no apps installed)',
          fills.length ? `Fills:\n${fills.join('\n')}` : '(no fills installed)',
          renderers.length
            ? `Renderers:\n${renderers.join('\n')}`
            : '(nothing claims a leaf kind; the shipped thread draws sessions)',
          layouts.length ? `Layouts:\n${layouts.join('\n')}` : '(no layouts)',
        ].join('\n\n')
      },
    },
    generate_app_icons: {
      description:
        'Give installed apps their stored icons (RFC 0332). Without restyle: ' +
        'every app that has no stored icon gets one generated from what it is ' +
        '(its name, description and type) and written to its catalog entry; ' +
        'apps that already have one are left alone. With restyle: true, write ' +
        'a NEW balanced set for every app, colours spread so no two neighbours ' +
        "share one and codes distinct, keeping each app's letter and drawing. " +
        'Use restyle when a person asks for their own icon set or a fresh look; ' +
        'pass a different seed for a different set.',
      inputSchema: {
        type: 'object',
        properties: {
          restyle: {
            type: 'boolean',
            description: 'Write a new balanced set for every app.',
          },
          seed: {
            type: 'string',
            description:
              'Picks the set when restyling; a new seed, a new set. Defaults to now.',
          },
        },
      },
      execute: async (a) => {
        const restyle = a.restyle === true
        const seed = a.seed == null ? String(Date.now()) : String(a.seed)
        // The apps a person sees as tiles: not layouts, not chrome, and not a
        // thread renderer (whose `handles` is a leaf kind rather than a mime).
        const apps = (await readAll()).filter(
          (x) =>
            x.presentation !== 'layout' &&
            !(x.panels ?? []).some((c) => c.slot) &&
            (!x.handles?.length || x.handles.some((h) => h.includes('/'))),
        )
        if (apps.length === 0) return 'No apps installed.'
        const iconApp = (x: (typeof apps)[number]) => ({
          id: x.id,
          name: x.name,
          ...(x.dataType ? { dataType: x.dataType } : {}),
          ...(x.description ? { description: x.description } : {}),
          ...(x.icon ? { icon: x.icon } : {}),
        })
        const next = restyle
          ? restyleIcons(apps.map(iconApp), seed)
          : new Map(
              apps
                .filter((x) => !x.icon)
                .map((x) => [x.id, cleanIconSpec(generateIcon(iconApp(x)))]),
            )
        const written: string[] = []
        for (const x of apps) {
          const icon = next.get(x.id)
          if (!icon) continue
          await updateApp(repo, opts.projectId, x.id, {
            icon,
            // The catalog that holds the entry: a World app is updated there.
            ...(x.scope === 'world' && opts.worldId
              ? { catalogId: opts.worldId }
              : {}),
          })
          written.push(`${x.name}: ${JSON.stringify(icon)}`)
        }
        if (written.length === 0)
          return 'Every installed app already has a stored icon. Pass restyle: true for a new set.'
        return `${restyle ? 'Restyled' : 'Stored icons for'} ${written.length} app${written.length === 1 ? '' : 's'}:\n${written.join('\n')}`
      },
    },
  }
}

function describe(slot: Slot, fill: string[] | undefined): string {
  if (fill === undefined) return `uses the built-in ${slot}`
  if (fill.length === 0) return `has no ${slot}`
  return `puts ${fill.join(', ')} in the ${slot}`
}
